import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { GraduationCap, Brain, Users, Sparkles, MessageSquare, Layers, TrendingUp, Target, BarChart } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import TeacherLogin from "../components/LoginModules/TeacherLogin";
import { RateReview, Timeline, MenuBook, Group, Insights } from "@mui/icons-material";

const HeroSection = () => {

  // 👇 Add these lines inside the HeroSection component
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      icon: RateReview,
      title: "Generate Feedback Comments",
      description: "Generate detailed, personalized feedback comments that reflect student understanding and growth.",
      features: ["Personalized feedback", "Growth-oriented insights", "Student-focused"]
    },
    {
      icon: Layers,
      title: "Assessment based on DOK & Proficiency Scale",
      description: "Incorporate depth of knowledge levels & proficiency scale grading into your assessment.",
      features: ["DOK Level-based assessment", "Proficiency-based grading", "Standards alignment"]
    },
    {
      icon: MenuBook,
      title: "Curriculum Builder",
      description: "Design and manage subjects, units, and DOK-level skills in a centralized flow.",
      features: ["Organize subjects & units", "Structured curriculum flow", "Skill outcome mapping"]
    },
    {
      icon: Group,
      title: "Assess Multiple Students",
      description: "Record your assessment for all students efficiently in a few clicks.",
      features: ["Batch assessments", "Outcome tracking", "Efficient recording"]
    },
    {
      icon: Insights,
      title: "Smart Student Analytics",
      description: "Visualize learning gaps and strengths across DOK levels and subjects using dynamic charts.",
      features: ["Data-driven insights", "Interactive dashboards", "Learning gap detection"]
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const navigate = useNavigate();
  const [showTeacherLogin, setShowTeacherLogin] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <section id="home" className="min-h-screen relative overflow-hidden bg-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 animate-float">
          <GraduationCap size={60} className="text-secondary" />
        </div>
        <div className="absolute top-40 right-20 animate-float" style={{ animationDelay: '2s' }}>
          <Brain size={50} className="text-secondary" />
        </div>
        <div className="absolute bottom-40 left-20 animate-float" style={{ animationDelay: '4s' }}>
          <Users size={55} className="text-secondary" />
        </div>
        <div className="absolute bottom-20 right-10 animate-float" style={{ animationDelay: '1s' }}>
          <Sparkles size={45} className="text-secondary" />
        </div>
      </div>

      <div className="container mx-auto px-4 pt-24 pb-12 min-h-screen flex items-center">
        <motion.div
          className="grid lg:grid-cols-2 gap-12 items-center w-full"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Content */}
          <div className="text-secondary space-y-8">
            <motion.div variants={itemVariants}>
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">

                <span className="block text-cream text-3xl lg:text-5xl" > Supporting Teachers in Qualitative Assessment</span>
              </h1>
            </motion.div>

            <motion.p
              className="text-xl lg:text-2xl text-secondary/90 leading-relaxed"
              variants={itemVariants}
            >
              Grading practices are shifting from numerical scores to qualitative assessment. Gradely supports this transition with AI-powered tools that help teachers evaluate student learning more meaningfully.
            </motion.p>
            {/* 
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              variants={itemVariants}
            >
              <Button
                size="lg"
                className="bg-[#DCD9CA] text-primary hover:bg-[#DCD9CA] hover:text-primary text-lg px-8 py-4 rounded-full cursor-pointer transition-colors relative z-20"
                onClick={() => setShowTeacherLogin(true)}
              >
                Sign In To Start Free Trial
              </Button>
            </motion.div>

*/}
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              variants={itemVariants}
            >
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#f5f2e7] to-[#eae5d6] text-primary font-semibold hover:from-[#ede8d8] hover:to-[#ddd8c3] text-lg px-8 py-4 rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 ease-in-out"
                onClick={() => setShowTeacherLogin(true)}
              >
                <span className="flex items-center gap-2">

                  <span className="font-medium">Sign In</span>
                  <span className="opacity-50">||</span>
                  <span className="font-bold text-primary">Start Free Trial</span>
                </span>
              </Button>
            </motion.div>
            {/** 
            <motion.div
              className="grid grid-cols-3 gap-6 pt-8"
              variants={itemVariants}
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-cream"></div>
                <div className="text-secondary/80">Teachers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cream"></div>
                <div className="text-secondary/80">Students</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cream">95%</div>
                <div className="text-secondary/80">Satisfaction</div>
              </div>
            </motion.div>
            */}
          </div>

          {/* Modern Feature Slider */}
          <motion.div
            className="relative w-full"
            variants={itemVariants}
          >
            <div className="relative bg-[#e2e2d2] backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-primary/20 shadow-xl">
              {/* Slider Container */}
              <div className="relative h-80 sm:h-80 md:h-96 w-full overflow-hidden rounded-xl sm:rounded-2xl">
                {slides.map((slide, index) => {
                  const IconComponent = slide.icon;
                  return (
                    <motion.div
                      key={index}
                      className="absolute inset-0 w-full h-full"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{
                        opacity: index === currentSlide ? 1 : 0,
                        x: index === currentSlide ? 0 : 50
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      <div className="h-full flex flex-col justify-center items-center text-center p-3 sm:p-4 lg:p-6">
                        {/* Icon Container */}
                        <motion.div
                          className="mb-4 sm:mb-6 lg:mb-8 p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/15 to-primary/8 border border-primary/25"
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{
                            scale: index === currentSlide ? 1 : 0.8,
                            opacity: index === currentSlide ? 1 : 0
                          }}
                          transition={{ delay: 0.2, duration: 0.5 }}
                        >
                          <IconComponent size={32} className="sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-primary" />
                        </motion.div>

                        {/* Title */}
                        <motion.h3
                          className="text-lg sm:text-xl lg:text-2xl font-bold text-primary mb-2 sm:mb-3 lg:mb-4 px-2"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{
                            y: index === currentSlide ? 0 : 20,
                            opacity: index === currentSlide ? 1 : 0
                          }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                        >
                          {slide.title}
                        </motion.h3>

                        {/* Description */}
                        <motion.p
                          className="text-sm sm:text-base lg:text-lg text-foreground/80 leading-relaxed mb-4 sm:mb-5 lg:mb-6 max-w-xs sm:max-w-sm lg:max-w-md px-2"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{
                            y: index === currentSlide ? 0 : 20,
                            opacity: index === currentSlide ? 1 : 0
                          }}
                          transition={{ delay: 0.4, duration: 0.5 }}
                        >
                          {slide.description}
                        </motion.p>

                        {/* Feature List */}
                        <motion.div
                          className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{
                            y: index === currentSlide ? 0 : 20,
                            opacity: index === currentSlide ? 1 : 0
                          }}
                          transition={{ delay: 0.5, duration: 0.5 }}
                        >
                          {slide.features.map((feature, featureIndex) => (
                            <motion.span
                              key={featureIndex}
                              className="px-2 sm:px-3 py-1 bg-primary/15 text-primary text-xs sm:text-sm font-medium rounded-full border border-primary/25 whitespace-nowrap"
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{
                                scale: index === currentSlide ? 1 : 0.8,
                                opacity: index === currentSlide ? 1 : 0
                              }}
                              transition={{
                                delay: 0.6 + featureIndex * 0.1,
                                duration: 0.3
                              }}
                            >
                              {feature}
                            </motion.span>
                          ))}
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Progress Bar */}
              <div className="mt-4 sm:mt-5 lg:mt-6">
                {/* <div className="flex justify-between items-center mb-2">
                  <span className="text-xs sm:text-sm text-foreground/60">
                    {currentSlide + 1} of {slides.length}
                  </span>
                  <span className="text-xs sm:text-sm text-primary font-medium truncate max-w-32 sm:max-w-40 lg:max-w-48">
                    {slides[currentSlide].title}
                  </span>
                </div> */}
                <div className="w-full bg-primary/20 rounded-full h-1">
                  <motion.div
                    className="bg-primary h-1 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center gap-2 sm:gap-3 lg:gap-4 mt-4 sm:mt-5 lg:mt-6">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`relative group transition-all duration-300 ${index === currentSlide ? 'scale-110' : 'hover:scale-105'
                      }`}
                  >
                    <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${index === currentSlide
                      ? 'bg-primary shadow-lg shadow-primary/30'
                      : 'bg-primary/40 hover:bg-primary/60'
                      }`} />
                    {index === currentSlide && (
                      <motion.div
                        className="absolute -inset-1 sm:-inset-2 rounded-full border-2 border-primary/30"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-secondary rounded-full flex justify-center">
          <div className="w-1 h-3 bg-secondary rounded-full mt-2"></div>
        </div>
      </motion.div>

      {/* Teacher Login Modal */}
      {showTeacherLogin && (
        <TeacherLogin
          onLogin={() => navigate("/teacher-dashboard")}
          onClose={() => setShowTeacherLogin(false)}
        />
      )}
    </section>
  );
};

export default HeroSection;
